import {jsPDF} from 'jspdf'
import {AllianzNeoW04RegularNormal} from '../assets/fonts/AllianzNeoW04-Regular-normal'
import {AllianzNeoW04SemiBoldNormal} from '../assets/fonts/AllianzNeoW04-SemiBold-normal'

interface MultiColorTextOptions {
    startX?: number
    startY?: number
    lineHeight?: number
    maxWidth?: number
    handleNewLines?: boolean
}

interface TextPosition {
    x: number
    y: number
}

export const addMultiColorText = function (
    pdf: jsPDF,
    text: string,
    colorChangeIndex: number,
    color1: string,
    color2: string,
    options: MultiColorTextOptions = {}
): TextPosition {
    const {
        startX = 10,
        startY = 20,
        lineHeight = 7,
        maxWidth = pdf.internal.pageSize.width - 20,
        handleNewLines = true,
    } = options

    let currentX = startX
    let currentY = startY

    const parts = [
        {text: text.substring(0, colorChangeIndex), color: color1},
        {text: text.substring(colorChangeIndex), color: color2},
    ]

    const processTextSegment = (segment: string, color: string) => {
        pdf.setTextColor(color)

        if (!handleNewLines) {
            // add text - without multiline support
            pdf.text(segment, currentX, currentY)
            currentX += (pdf.getStringUnitWidth(segment) * pdf.getFontSize()) / pdf.internal.scaleFactor
            return
        }

        // support multiline
        const lines = segment.split('\n')

        for (let i = 0; i < lines.length; i++) {
            if (i > 0) {
                currentX = startX
                currentY += lineHeight
            }

            const wrappedLines = pdf.splitTextToSize(lines[i], maxWidth - (currentX - startX))

            for (let j = 0; j < wrappedLines.length; j++) {
                if (j > 0) {
                    currentX = startX
                    currentY += lineHeight
                }

                pdf.text(wrappedLines[j], currentX, currentY)
                currentX +=
                    (pdf.getStringUnitWidth(wrappedLines[j]) * pdf.getFontSize()) / pdf.internal.scaleFactor
            }
        }
    }

    parts.forEach((part) => processTextSegment(part.text, part.color))

    return {x: currentX, y: currentY}
}

export type PdfFontType = 'AllianzNeo-400' | 'AllianzNeo-600'

export const readCustomFonts = function (pdf: jsPDF): void {
    pdf.addFileToVFS('AllianzNeo-normal.ttf', AllianzNeoW04RegularNormal)
    pdf.addFont('AllianzNeo-normal.ttf', 'AllianzNeoNormal', 'normal')

    pdf.addFileToVFS('AllianzNeo-semi-bold-normal.ttf', AllianzNeoW04SemiBoldNormal)
    pdf.addFont('AllianzNeo-semi-bold-normal.ttf', 'AllianzNeoSemiBoldNormal', 'normal')
}

export const setCustomFont = function (pdf: jsPDF, type: PdfFontType = 'AllianzNeo-400') {
    if (type == 'AllianzNeo-400') {
        pdf.setFont('AllianzNeoNormal')
    } else if (type == 'AllianzNeo-600') {
        pdf.setFont('AllianzNeoSemiBoldNormal')
    }
}

/**
 * Function to add text with hyperlinks to PDF document
 * This function splits long texts into paragraphs while maintaining hyperlink functionality
 *
 * @param doc - jsPDF document instance
 * @param text - Text to be added (can contain multiple paragraphs)
 * @param x - X position for text placement
 * @param y - Y position for text placement (starting point)
 * @param options - Configuration options for text rendering
 * @returns Final Y position after all text has been added
 */
export const addLinkedText = function (
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    options: {
        maxWidth: number // Maximum width for text wrapping
        lineHeight: number // Height between lines
        paragraphSpacing: number // Extra spacing between paragraphs
        fontSize: number // Font size
        links: Array<{
            // Array of link definitions
            text: string // Text that should be clickable
            url: string // URL to link to
        }>
    }
): number {
    // Set font size
    doc.setFontSize(options.fontSize)

    // Current vertical position tracker
    let currentY = y

    // Split text into paragraphs (based on double newlines)
    const paragraphs = text.split(/\n\s*\n/)

    // Process each paragraph
    for (let i = 0; i < paragraphs.length; i++) {
        const paragraph = paragraphs[i].trim()

        // Skip empty paragraphs
        if (!paragraph) continue

        // Split paragraph into lines that fit within maxWidth
        const lines = doc.splitTextToSize(paragraph, options.maxWidth)

        // Process each line
        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            const line = lines[lineIndex]

            // Check if we need to move to a new page
            if (currentY > doc.internal.pageSize.height - 20) {
                doc.addPage()
                currentY = 20 // Reset Y position on new page
            }

            // Check if the line contains any of our link texts
            let remainingLine = line
            let currentX = x

            while (remainingLine.length > 0) {
                // Find the earliest link in the current line (if any)
                let earliestLinkIndex = -1
                let earliestLinkPosition = Infinity
                let currentLink:any = null

                for (const link of options.links) {
                    const linkPosition = remainingLine.indexOf(link.text)
                    if (linkPosition !== -1 && linkPosition < earliestLinkPosition) {
                        earliestLinkPosition = linkPosition
                        earliestLinkIndex = options.links.indexOf(link)
                        currentLink = link
                    }
                }

                if (earliestLinkIndex === -1) {
                    // No links in the remaining text, just add it normally
                    doc.text(remainingLine, currentX, currentY)
                    remainingLine = ''
                } else {
                    // There's a link in the text

                    // Add text before the link
                    const beforeLinkText = remainingLine.substring(0, earliestLinkPosition)
                    if (beforeLinkText) {
                        doc.text(beforeLinkText, currentX, currentY)
                        // Move X position forward
                        currentX += doc.getTextWidth(beforeLinkText)
                    }

                    // Add the linked text
                    const linkText = currentLink.text

                    // Save the current text color
                    const currentTextColor = doc.getTextColor()

                    // Set link text color and style (blue and underlined as convention)
                    doc.setTextColor('#414141')
                    doc.text(linkText, currentX, currentY)

                    // Calculate link width and height for the clickable area
                    const linkWidth = doc.getTextWidth(linkText)

                    // Add link annotation
                    doc.link(currentX, currentY - options.fontSize / 2, linkWidth, options.fontSize, {
                        url: currentLink.url,
                    })

                    // Restore text color
                    doc.setTextColor(currentTextColor)

                    // Move X position forward
                    currentX += linkWidth

                    // Update remaining line
                    remainingLine = remainingLine.substring(earliestLinkPosition + linkText.length)
                }
            }

            // Move to next line
            currentY += options.lineHeight
        }

        // Add paragraph spacing (except after the last paragraph)
        if (i < paragraphs.length - 1) {
            currentY += options.paragraphSpacing
        }
    }

    // Return the final Y position
    return currentY
}

/**
 * Extracts text content and links from an HTML string
 * Assumes input contains only <p> and <a href> tags
 */
export const extractTextAndLinks = function (html: string) {
    const links: {text: string; url: string}[] = []
    let linkMatch
    const linkRegex = /<a href="([^"]+)">([^<]+)<\/a>/g

    while ((linkMatch = linkRegex.exec(html)) !== null) {
        links.push({url: linkMatch[1], text: linkMatch[2]})
    }

    const paragraphs =
        html
            .replace(/<a href="[^"]+">([^<]+)<\/a>/g, '$1')
            .match(/<p>(.*?)<\/p>/g)
            ?.map((p) => p.replace(/<\/?p>/g, '').trim()) || []

    paragraphs.forEach((paragraph, index) => {
        paragraphs[index] = paragraph.replace(/&nbsp;/g, "\u00A0")
    })

    return {
        content: paragraphs.join('\n\n'),
        links,
    }
}
